import React, { useState, useEffect } from "react";
import { X, MapPin, CheckCircle, XCircle, Loader } from "lucide-react";
import { useNavigate } from "react-router-dom";
import CityManager from "../../../services/home/CityManager";
import toast from "react-hot-toast";

export const ServiceabilityCheckModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [selectedCity, setSelectedCity] = useState("");
  const [pincodeInput, setPincodeInput] = useState("");
  const [cities, setCities] = useState([]);
  const [allPincodes, setAllPincodes] = useState([]); // Store all pincodes from selected city
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // null, "available", "not_available"
  const [step, setStep] = useState("input"); // "input" or "result"
  const [foundPincode, setFoundPincode] = useState(null);

  // Fetch cities on mount
  useEffect(() => {
    if (isOpen) {
      CityManager.getActiveCitiesWithPincodes()
        .then((data) => {
          setCities(data || []);
        })
        .catch(() => {
          toast.error("Failed to load cities");
        });
    }
  }, [isOpen]);

  // Update pincodes when city changes
  useEffect(() => {
    if (selectedCity) {
      const city = cities.find((c) => c.cityId === parseInt(selectedCity));
      setAllPincodes(city?.pincodes || []);
      setPincodeInput(""); // Reset pincode input
    } else {
      setAllPincodes([]);
    }
  }, [selectedCity, cities]);

  const handleCheck = async () => {
    if (!selectedCity || !pincodeInput.trim()) {
      toast.error("Please select city and enter pincode");
      return;
    }

    // Validate pincode format (should be 6 digits)
    const pincodeRegex = /^\d{6}$/;
    if (!pincodeRegex.test(pincodeInput.trim())) {
      toast.error("Please enter a valid 6-digit pincode");
      return;
    }

    setLoading(true);
    try {
      // Find the selected city
      const city = cities.find((c) => c.cityId === parseInt(selectedCity));
      
      // Check if the entered pincode exists in the city's pincodes
      const pincode = allPincodes.find((p) => p.pinCode === pincodeInput.trim());

      if (pincode && city && city.active && pincode.active) {
        setResult("available");
        setFoundPincode(pincode);
      } else {
        setResult("not_available");
        setFoundPincode(null);
      }
      setStep("result");
    } catch (error) {
      toast.error("Error checking serviceability");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSelectedCity("");
    setPincodeInput("");
    setResult(null);
    setStep("input");
    setFoundPincode(null);
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handleContactUs = () => {
    handleClose();
    navigate("/contact-us");
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 mt-20">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative">
        {/* Close Button - Only show in input step or when result is available */}
        {(step === "input" || result === "available") && (
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition"
          >
            <X size={24} />
          </button>
        )}

        {step === "input" ? (
          <>
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
              <MapPin className="text-primary" size={28} />
              <h2 className="text-2xl font-bold text-gray-800">
                Check Serviceability
              </h2>
            </div>

            {/* City Selection */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
               City
              </label>
              <select
                value={selectedCity}
                onChange={(e) => setSelectedCity(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition"
              >
                <option value="">Select a city</option>
                {cities.map((city) => (
                  <option key={city.cityId} value={city.cityId}>
                    {city.cityName}, {city.state}
                  </option>
                ))}
              </select>
            </div>

            {/* Pincode Input */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Enter Pincode
              </label>
              <input
                type="text"
                value={pincodeInput}
                onChange={(e) => {
                  // Only allow digits and limit to 6 characters
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setPincodeInput(value);
                }}
                placeholder="Enter 6-digit pincode"
                maxLength={6}
                disabled={!selectedCity}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
              <p className="text-xs text-gray-500 mt-1">
                {selectedCity ? "Enter your 6-digit pincode" : "Please select a city first"}
              </p>
            </div>

            {/* Check Button */}
            <button
              onClick={handleCheck}
              disabled={!selectedCity || !pincodeInput || pincodeInput.length !== 6 || loading}
              className="w-full bg-primary text-white py-3 rounded-lg font-semibold hover:bg-orange-600 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader size={18} className="animate-spin" />
                  Checking...
                </>
              ) : (
                "Check Availability"
              )}
            </button>
          </>
        ) : (
          <>
            {/* Result Display */}
            <div className="text-center">
              {result === "available" ? (
                <>
                  <CheckCircle className="text-green-500 mx-auto mb-4" size={64} />
                  <h3 className="text-2xl font-bold text-green-600 mb-2">
                    Delivery Available! 🎉
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Great news! We deliver to{" "}
                    <span className="font-semibold">
                      {cities.find((c) => c.cityId === parseInt(selectedCity))?.cityName}
                    </span>{" "}
                    at pincode{" "}
                    <span className="font-semibold text-primary">
                      {pincodeInput}
                    </span>
                  </p>
                </>
              ) : (
                <>
                  <XCircle className="text-red-500 mx-auto mb-4" size={64} />
                  <h3 className="text-2xl font-bold text-red-600 mb-2">
                    Pincode Not Available
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Sorry, we don't currently deliver to pincode{" "}
                    <span className="font-semibold text-red-600">
                      {pincodeInput}
                    </span>{" "}
                    in {cities.find((c) => c.cityId === parseInt(selectedCity))?.cityName}.
                    <br />
                    <span className="text-sm">
                      Please check another pincode or contact us to request service in your area.
                    </span>
                  </p>
                </>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={handleReset}
                  className="flex-1 bg-gray-200 text-gray-800 py-2.5 rounded-lg font-semibold hover:bg-gray-300 transition"
                >
                  Check Another
                </button>
                {result === "available" ? (
                  <button
                    onClick={handleClose}
                    className="flex-1 bg-primary text-white py-2.5 rounded-lg font-semibold hover:bg-orange-600 transition"
                  >
                    Close
                  </button>
                ) : (
                  <button
                    onClick={handleContactUs}
                    className="flex-1 bg-primary text-white py-2.5 rounded-lg font-semibold hover:bg-orange-600 transition"
                  >
                    Contact Us
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ServiceabilityCheckModal;
